import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  styled,
  useTheme,
} from "@mui/material";
import { Fragment, useContext, useState } from "react";
import { useApi } from "@platform/api";
import { Button, Link, paletteVar, Typography } from "@platform/design-system";

import { ConfirmDialog } from "../../../../components";
import { DOCUMENT_NAMES } from "../../../../constants/rfp";
import { useToastNotificationService } from "../../../../hooks";
import { downloadBinaryFile } from "../../../../utils";
import { DownloadTypography } from "../../components/styledComponents";
import { RFP_ACTION_TYPES } from "../../constants";
import { RfpContext } from "../../contexts";

import { CensusFileCheckRow } from "./CensusFileCheckRow";
import AssignCoverages from "./modals/assignCoverages";
import { useCensusCheckForBroker } from "./useCensusCheckForBroker";

const {
  REMOVE_STATE_RELATED_DOCUMENTS,
  REMOVE_STATE_RELATED_DOCUMENTS_ALL_COVERAGES,
} = RFP_ACTION_TYPES;
const CoverageAssignmentList = ({
  files,
  documentType,
  uploadedFiles,
  removeFile,
}) => {
  const uniqueFiles = files.reduce(
    (acc, file) => {
      const name = file.name || file.path; // Prefer 'name' if available, fallback to 'path'

      if (!acc.uniqueFileNames.has(name)) {
        acc.uniqueFileNames.add(name);
        acc.list.push(file);
      }

      return acc;
    },
    { uniqueFileNames: new Set(), list: [] },
  ).list;

  const isCensusPanel = documentType.documentTypeName === DOCUMENT_NAMES.CENSUS;
  const checksCensus = useCensusCheckForBroker() && isCensusPanel;
  const documentIdOf = (file) =>
    uploadedFiles.find((uploaded) => uploaded.fileName === file.name)?.file?.id;
  const documentIds = uniqueFiles.map(documentIdOf).filter(Boolean);
  const newestDocumentId = Math.max(...documentIds);
  const [documentIdsAtOpen] = useState(() => new Set(documentIds));

  return (
    <Box>
      <Box display="flex" flexDirection="column" gap={2}>
        <Box border="1px solid rgba(224, 224, 224, 1)">
          <Table>
            <TableHead
              sx={{
                background: paletteVar("surfaces-primary", "#EFF8FB"),
              }}
            >
              <TableCell>
                <Typography variant="body-medium">
                  <strong>Uploaded Documents</strong>
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body-medium">
                  <strong>Assigned Coverages</strong>
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body-medium">
                  <strong></strong>
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body-medium">
                  <strong></strong>
                </Typography>
              </TableCell>
            </TableHead>
            <TableBody>
              {uniqueFiles.length ? (
                uniqueFiles.map((item) => {
                  const documentId = documentIdOf(item);

                  return (
                    <Fragment key={item.name}>
                      <CoverageAssignmentItem
                        uploadedFiles={uploadedFiles}
                        file={item}
                        documentType={documentType}
                        removeFile={removeFile}
                      />
                      {checksCensus && documentId && (
                        <CensusFileCheckRow
                          documentId={documentId}
                          isNewest={documentId === newestDocumentId}
                          isJustUploaded={!documentIdsAtOpen.has(documentId)}
                        />
                      )}
                    </Fragment>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell>
                    <Typography variant="body-medium" color="textSecondary">
                      <em>No uploaded documents</em>
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      </Box>
    </Box>
  );
};

const CoverageAssignmentItem = ({
  file,
  uploadedFiles,
  documentType,
  removeFile,
}) => {
  useTheme();
  const documentTypeName = documentType.documentTypeName;
  const fileDocumentTypes =
    uploadedFiles.find((f) => f.fileName === file.name)?.documentTypes || {};
  const isFileInAnotherDocumentType = Object.keys(fileDocumentTypes).length > 1;
  const fileCoverages = fileDocumentTypes[documentTypeName] || [];
  const hasCoveragesAssigned = fileCoverages.length > 0;
  const [openConfirm, setOpenConfirm] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isEdditing, setIsEdditing] = useState(false);
  const { setRfp, updateRFPDocument, handleSaveDraft, deleteRFPDocument } =
    useContext(RfpContext);
  const axiosInstance = useApi();
  const { createToastNotification } = useToastNotificationService();

  const handleEdditCoverage = () => {
    setIsEdditing(true);
    setSelectedFile(file);
  };

  const onRemove = async () => {
    const filesToBeUpdated = [];
    setRfp({
      type: REMOVE_STATE_RELATED_DOCUMENTS,
      value: { fileName: file.name, documentTypeName },
    });
    const currentFile = uploadedFiles.find((f) => f.fileName === file.name);
    const localDocumentTypes = Object.keys(currentFile.documentTypes || {});
    filesToBeUpdated.push({
      id: currentFile.file.id,
      documentType: localDocumentTypes.filter(
        (type) => type !== documentTypeName,
      ),
    });
    await updateRFPDocument(filesToBeUpdated);
    await handleSaveDraft(false, true);
    removeFile(file.name);
    setOpenConfirm(false);
  };

  const onDelete = async () => {
    const currentFile = uploadedFiles.find((f) => f.fileName === file.name);
    const fileToBeDeleted = {
      id: currentFile.file.id,
      name: currentFile.fileName,
      fileName: currentFile.fileName,
    };
    setRfp({
      type: REMOVE_STATE_RELATED_DOCUMENTS_ALL_COVERAGES,
      value: fileToBeDeleted,
    });
    await deleteRFPDocument(fileToBeDeleted);
    await handleSaveDraft(false, true);
    removeFile(file.name);
    setOpenConfirm(false);
  };

  const handleDownload = async (file) => {
    try {
      if (file instanceof File) {
        downloadBinaryFile(file, file.name);
      } else {
        const { data } = await axiosInstance.post(
          `brokerportal/v1/getRfpFile`,
          {
            downloadLink: file.downloadLink,
          },
          {
            responseType: "blob",
          },
        );

        downloadBinaryFile(data, file.name);
      }
    } catch {
      createToastNotification({
        severity: "error",
        message: `Unable to download file ${file.name}. Please try again.`,
        title: "Couldn't download file",
      });
    }
  };

  return (
    <>
      <TableRow id={documentTypeName}>
        <TableCell sx={{ maxWidth: "300px" }}>
          <DownloadTypography onClick={() => handleDownload(file)}>
            {file.name}
          </DownloadTypography>
        </TableCell>
        <TableCell>
          {hasCoveragesAssigned && fileCoverages.length ? (
            fileCoverages.map((item) => (
              <Typography key={item} id={item} variant="body-large">
                {item}
              </Typography>
            ))
          ) : (
            <Button
              variant="primary"
              startIcon={<AddIcon />}
              onClick={() => setSelectedFile(file)}
            >
              Assign Coverages
            </Button>
          )}
        </TableCell>
        <TableCell>
          {hasCoveragesAssigned ? (
            <Link
              component="button"
              onClick={handleEdditCoverage}
              underline="none"
            >
              <EditOutlinedIcon />
            </Link>
          ) : (
            <p>
              <Typography variant="body-medium"></Typography>
            </p>
          )}
        </TableCell>
        <TableCell>
          <Link
            component="button"
            onClick={() => {
              if (isFileInAnotherDocumentType) {
                setOpenConfirm(true);
              } else {
                onDelete();
              }
            }}
            underline="none"
          >
            <DeleteOutlineIcon />
          </Link>
        </TableCell>
        {openConfirm && (
          <ConfirmDialog
            title="Remove Document"
            message={
              <Typography>
                <strong>{file.name}</strong> file is currently uploaded to
                multiple Document Types.
              </Typography>
            }
            isOpen={openConfirm}
            closeBtnMsg="Cancel"
            onClose={() => setOpenConfirm(false)}
            subMessage={
              <Box>
                <Typography>
                  Select below whether you want to remove it only from{" "}
                  <strong>{documentTypeName}</strong> or from all document types
                  by deleting the file&mdash;
                </Typography>
                <StyledBox>
                  <Button variant="primary" onClick={onRemove}>
                    Remove From Document Type
                  </Button>
                  <Button onClick={onDelete} variant="secondary" sx={{ ml: 2 }}>
                    Delete File
                  </Button>
                </StyledBox>
              </Box>
            }
          />
        )}
      </TableRow>
      {!!selectedFile && (
        <AssignCoverages
          file={selectedFile}
          uploadedFiles={uploadedFiles}
          closeModal={() => setSelectedFile(null)}
          documentType={documentType}
          fileCoverages={fileCoverages}
          isEdditing={isEdditing}
        />
      )}
    </>
  );
};

const StyledBox = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(3),
}));

export default CoverageAssignmentList;
